<template>
  <transition name="fade">
    <div v-if="show" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style="z-index: 9999;">
      <div class="bg-white rounded-lg p-6 sm:p-8 mx-4 flex flex-col items-center space-y-4 max-w-sm">
        <!-- Spinner -->
        <div class="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-4 border-[#4ECF84]"></div>

        <!-- Loading text with rotation -->
        <div class="text-center">
          <p class="text-base sm:text-lg font-medium text-gray-900 font-['Lexend']">{{ currentMessage }}</p>
          <p v-if="subMessage" class="text-sm text-gray-600 font-['Lexend'] mt-1">{{ subMessage }}</p>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch, onUnmounted } from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  message: {
    type: String,
    default: 'Loading...'
  },
  subMessage: {
    type: String,
    default: null
  },
  rotateMessages: {
    type: Boolean,
    default: true
  }
});

// Rotating message states
const searchMessages = [
  'Searching for destinations...',
  'Analyzing travel options...',
  'Finding the best deals...',
  'Checking availability...',
  'Calculating costs...',
  'Almost there...'
];

const currentMessage = ref(props.message);
let messageIndex = 0;
let rotationInterval = null;

// Rotate messages every 2.5 seconds
const startMessageRotation = () => {
  if (!props.rotateMessages) {
    currentMessage.value = props.message;
    return;
  }

  // Check if the main message suggests we should rotate
  const shouldRotate = props.message.toLowerCase().includes('search') ||
                      props.message.toLowerCase().includes('loading') ||
                      props.message.toLowerCase().includes('finding');

  if (shouldRotate) {
    currentMessage.value = searchMessages[0];
    messageIndex = 0;

    rotationInterval = setInterval(() => {
      messageIndex = (messageIndex + 1) % searchMessages.length;
      currentMessage.value = searchMessages[messageIndex];
    }, 2500); // Change message every 2.5 seconds
  } else {
    currentMessage.value = props.message;
  }
};

const stopMessageRotation = () => {
  if (rotationInterval) {
    clearInterval(rotationInterval);
    rotationInterval = null;
  }
};

// Watch for show prop changes
watch(() => props.show, (isShowing) => {
  if (isShowing) {
    startMessageRotation();
  } else {
    stopMessageRotation();
  }
}, { immediate: true });

// Watch for message changes
watch(() => props.message, () => {
  if (props.show) {
    stopMessageRotation();
    startMessageRotation();
  }
});

// Cleanup on unmount
onUnmounted(() => {
  stopMessageRotation();
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
